import { Link } from '@inertiajs/react';
import { ExternalLink, Users } from 'lucide-react';
import { useState } from 'react';
import { githubUsername } from '@/components/admin/github-username';
import { SubmissionStatusBadge } from '@/components/admin/submission-status-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Team } from '@/types';

interface TeamsTableProps {
  eventSlug: string;
  teams: Team[];
}

export function TeamsTable({ eventSlug, teams }: TeamsTableProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTeams = teams.filter((team) => {
    const query = searchQuery.toLowerCase();
    return (
      // Drizzle declares `slug` non-null, the column is not; an unnamed team is searchable
      // by its members only.
      (team.slug ?? '').toLowerCase().includes(query) ||
      team.members.some(
        (member) =>
          member.fullName.toLowerCase().includes(query) ||
          member.github?.toLowerCase().includes(query),
      )
    );
  });

  if (teams.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No teams found. Create one to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4">
        <Input
          placeholder="Search by team slug, hacker name, or GitHub username..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Team</TableHead>
              <TableHead>Track</TableHead>
              <TableHead>Mentor</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Submission</TableHead>
              <TableHead>Table #</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTeams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center">
                  <p className="text-muted-foreground">
                    No teams match your search
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredTeams.map((team) => (
                <TableRow key={team.id}>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">{team.slug}</span>
                      {team.formedOnSite && (
                        <Badge variant="secondary" className="w-fit">
                          Formed on-site
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {team.track ? (
                      <Link
                        href={`/admin/${eventSlug}/tracks/${team.track.slug}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {team.track.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {team.mentor ? (
                      <a
                        href={team.mentor.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                      >
                        {team.mentor.fullName}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {team.members.length === 0 ? (
                        <span className="text-muted-foreground text-sm">
                          No members
                        </span>
                      ) : (
                        team.members.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center gap-2"
                          >
                            <span className="text-sm">{member.fullName}</span>
                            {!member.entered && (
                              <Badge
                                variant="outline"
                                className="border-amber-300 bg-amber-100 text-amber-800"
                              >
                                No ha entrado
                              </Badge>
                            )}
                            {member.github && (
                              <a
                                href={member.github}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-blue-600 text-xs hover:text-blue-800"
                              >
                                @{githubUsername(member.github)}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <SubmissionStatusBadge status={team.submissionStatus} />
                  </TableCell>
                  <TableCell>
                    {team.tableNumber || (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" asChild>
                      <Link href={`/admin/${eventSlug}/teams/${team.slug}`}>
                        <Users className="mr-2 h-4 w-4" />
                        Manage
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
